import { useState, useCallback } from "react";
import { erp } from "@/lib/erp";
import type { ExportListParams } from "@/lib/erp/listExport";
import { toast } from "sonner";

/**
 * Hook for generic list export. Manages loading state, blob download with
 * UTF-8 BOM (for Thai text in Excel), and success/error toasts.
 *
 * Pattern copied from useExportPersonalizationCSV in orderHooks.ts.
 */
export function useExportList() {
  const [isExporting, setIsExporting] = useState(false);

  const exportList = useCallback(async (params: ExportListParams): Promise<boolean> => {
    setIsExporting(true);
    try {
      const result = await erp.listExport.exportList(params);

      // Handle empty data — warning toast, no download
      if (result.empty) {
        toast.warning("No rows match the current filters");
        return false;
      }

      // Build blob — base64 for xlsx, BOM-prefixed string for csv
      let blob: Blob;
      if (result.format === "base64") {
        const bytes = Uint8Array.from(atob(result.content), (c) =>
          c.charCodeAt(0),
        );
        blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      } else {
        blob = new Blob(["\uFEFF" + result.content], {
          type: "text/csv;charset=utf-8;",
        });
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link); // Required for Firefox
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const msg = result.truncated
        ? `Exported ${result.exported_count} of ${result.total_count} rows — narrow your filters for a complete export`
        : `Exported ${result.exported_count} rows as ${result.filename}`;
      toast.success(msg);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Export failed";
      toast.error(message);
      return false;
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportList, isExporting };
}
